import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';

export type GrowthSettings = {
  id: string;
  created_at: string;
  updated_at: string;
  our_site_urls: Record<string, string>;
  gbp_maps_url: string | null;
  peers: { name: string; site_urls: Record<string, string>; gbp_maps_url?: string }[];
  thresholds: Record<string, unknown>;
};

async function fetchSettings(): Promise<GrowthSettings | null> {
  const { data, error } = await supabase
    .from('growth_settings')
    .select('*')
    .limit(1)
    .single();
  if (error && error.code !== 'PGRST116') throw error;
  return data as GrowthSettings | null;
}

export function useGrowthSettings() {
  return useQuery({
    queryKey: ['growth', 'settings'],
    queryFn: fetchSettings,
  });
}

export function useGrowthSettingsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<GrowthSettings>) => {
      const { data: existing } = await supabase.from('growth_settings').select('id').limit(1).single();
      if (existing) {
        const { data, error } = await supabase
          .from('growth_settings')
          .update(payload)
          .eq('id', existing.id)
          .select()
          .single();
        if (error) throw error;
        return data;
      }
      const { data, error } = await supabase.from('growth_settings').insert(payload).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['growth', 'settings'] }),
  });
}
